import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { Quotes } from "@phosphor-icons/react";

/**
 * Anonymized note word-cloud.
 *
 * Reads `/api/public/note-cloud` — already PII-filtered, stop-word-stripped,
 * and aggregated server-side with a min-count floor. We just lay the words
 * out in a flow-wrap with font-sizes proportional to frequency and a slight
 * stagger animation on entry. No third-party word-cloud lib (overkill for 25
 * tokens), no SVG positioning math (would feel "designed by a machine").
 *
 * Hides itself when fewer than 4 words clear the server-side floor — empty
 * clouds look sad, so we'd rather show nothing.
 */
export default function NoteWordCloud() {
    const t = useT();
    const [data, setData] = useState(null);

    useEffect(() => {
        let live = true;
        api.get("/public/note-cloud")
            .then((r) => { if (live) setData(r.data); })
            .catch(() => { if (live) setData(null); });
        return () => { live = false; };
    }, []);

    const words = data?.words || [];
    if (!data) return null;
    if (words.length < 4) return null;

    // Map raw counts to a 1-5 size bucket so the largest word doesn't tower
    // over the smallest by 20× when one phrase dominates a small dataset.
    const counts = words.map((w) => w.count);
    const min = Math.min(...counts);
    const max = Math.max(...counts);
    const range = Math.max(max - min, 1);
    const bucket = (n) => Math.floor(((n - min) / range) * 4) + 1; // 1..5

    const sizeClass = {
        1: "text-base",
        2: "text-xl",
        3: "text-2xl sm:text-3xl",
        4: "text-3xl sm:text-4xl",
        5: "text-4xl sm:text-5xl",
    };

    const colorClass = {
        1: "text-[#4A5C50]",
        2: "text-[#2F4D3A]",
        3: "text-[#184A30]",
        4: "text-[#184A30]",
        5: "text-[#0F3320]",
    };

    return (
        <section
            className="border-t border-[#E5E0D8] bg-[#F5F0E5] py-14 sm:py-20"
            data-testid="note-word-cloud"
        >
            <div className="max-w-5xl mx-auto px-6">
                <div className="flex items-start gap-3">
                    <Quotes size={28} weight="duotone" className="text-[#184A30] shrink-0 mt-1" />
                    <div>
                        <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A5C50]">
                            {t("note_cloud.eyebrow")}
                        </p>
                        <h3 className="serif text-3xl sm:text-5xl text-[#112318] mt-2 leading-tight">
                            {t("note_cloud.title")}
                        </h3>
                        <p className="mt-3 text-sm text-[#4A5C50] max-w-xl leading-relaxed">
                            {t("note_cloud.subtitle", {
                                n: data.distinct_notes,
                                min: data.min_count,
                            })}
                        </p>
                    </div>
                </div>

                <div
                    className="mt-10 flex flex-wrap items-baseline justify-center gap-x-6 gap-y-3"
                    data-testid="note-word-cloud-words"
                >
                    {words.map((w, i) => {
                        const b = bucket(w.count);
                        return (
                            <span
                                key={w.text}
                                className={`serif fade-in transition-transform hover:scale-110 cursor-default ${sizeClass[b]} ${colorClass[b]}`}
                                style={{ animationDelay: `${i * 35}ms` }}
                                title={`${w.count}`}
                                data-testid={`note-word-${w.text}`}
                            >
                                {w.text}
                            </span>
                        );
                    })}
                </div>

                <p className="mt-10 text-center text-[11px] tracking-[0.2em] uppercase text-[#4A5C50]/70">
                    {t("note_cloud.footnote")}
                </p>

                {/* Language breakdown — anonymized stacked bar */}
                {data.languages && (data.languages.en + data.languages.vi + data.languages.other) >= 5 && (
                    <LanguageMix data={data.languages} t={t} />
                )}
            </div>
        </section>
    );
}

function LanguageMix({ data, t }) {
    const total = (data.en || 0) + (data.vi || 0) + (data.other || 0);
    if (total === 0) return null;
    const pct = (n) => Math.round(((n || 0) / total) * 100);
    const enPct = pct(data.en);
    const viPct = pct(data.vi);
    const otherPct = Math.max(0, 100 - enPct - viPct);
    const segments = [
        { key: "en", value: data.en, pct: enPct, color: "#184A30", label: "EN" },
        { key: "vi", value: data.vi, pct: viPct, color: "#CBA35C", label: "VI" },
        { key: "other", value: data.other, pct: otherPct, color: "#A89F88", label: t("note_cloud.lang_other") },
    ].filter((s) => s.pct > 0);

    return (
        <div className="mt-12 max-w-2xl mx-auto" data-testid="note-language-mix">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A5C50] text-center">
                {t("note_cloud.lang_eyebrow")}
            </p>

            <div className="mt-4 h-3 rounded-full overflow-hidden bg-[#E5E0D8] flex">
                {segments.map((s, i) => (
                    <div
                        key={s.key}
                        className="h-full transition-all duration-700 hover:opacity-80"
                        style={{
                            width: `${s.pct}%`,
                            background: s.color,
                            animationDelay: `${i * 80}ms`,
                        }}
                        title={`${s.label}: ${s.value} notes (${s.pct}%)`}
                        data-testid={`note-language-seg-${s.key}`}
                    />
                ))}
            </div>

            <div className="mt-4 flex flex-wrap justify-center gap-x-8 gap-y-2 text-[12px]">
                {segments.map((s) => (
                    <div key={s.key} className="flex items-center gap-2">
                        <span
                            className="w-2 h-2 rounded-full"
                            style={{ background: s.color }}
                            aria-hidden
                        />
                        <span className="font-semibold text-[#112318]">{s.label}</span>
                        <span className="text-[#4A5C50] mono">{s.pct}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
